/**
 * The Help guides (1.2.0): one entry per tab, in tab order, with who may see it. Help shows only the guides the
 * signed-in person can use; the others have no tab and no route. Visibility is by permission, never by role key, so
 * custom roles get the guides their permissions open.
 *
 * Order: the guides for using PRISM, then the administration guides, then API reference, and About last. Sequence
 * editor, Working with NLEs and Quality control stay next to each other in that order (the e2e tests check it).
 * Each guide is loaded when it is first opened.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  ArrowRightLeft, BadgeCheck, Braces, ClipboardList, Clapperboard, Compass, Copyright, FileCode2, Film, FolderHeart, HardDrive, Inbox, Info,
  Layers, Library, PlaySquare, Server, Shapes, ShieldCheck, Truck, UserRound, Workflow, type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@prism/contracts';

export type GuideGroup = 'using' | 'admin' | 'reference';

export interface Guide {
  /** The route under /help, also the tab's testid suffix (help-tab-<to>). */
  to: string;
  label: string;
  icon: LucideIcon;
  group: GuideGroup;
  /** The guide's root testid. */
  testId: string;
  visible: (perms: readonly Permission[]) => boolean;
  Component: LazyExoticComponent<ComponentType>;
}

const everyone = () => true;
const has = (p: Permission) => (perms: readonly Permission[]) => perms.includes(p);

export const GUIDES: readonly Guide[] = [
  { to: 'introduction', label: 'Introduction', icon: Compass, group: 'using', testId: 'guide-introduction', visible: everyone,
    Component: lazy(() => import('./guides/IntroductionGuide')) },
  { to: 'library', label: 'Library', icon: Library, group: 'using', testId: 'guide-library', visible: everyone,
    Component: lazy(() => import('./guides/LibraryGuide')) },
  { to: 'assets', label: 'Assets', icon: PlaySquare, group: 'using', testId: 'guide-assets', visible: everyone,
    Component: lazy(() => import('./guides/AssetsGuide')) },
  { to: 'collections', label: 'Collections & sharing', icon: FolderHeart, group: 'using', testId: 'guide-collections', visible: everyone,
    Component: lazy(() => import('./guides/CollectionsGuide')) },
  { to: 'tasks', label: 'Tasks & Monitor', icon: ClipboardList, group: 'using', testId: 'guide-tasks', visible: everyone,
    Component: lazy(() => import('./guides/TasksGuide')) },
  // Read-only viewers can open sequences too.
  { to: 'editor', label: 'Sequence editor', icon: Clapperboard, group: 'using', testId: 'guide-editor', visible: everyone,
    Component: lazy(() => import('./guides/EditorGuide')) },
  // As Send to edit itself requires.
  { to: 'nle', label: 'Working with NLEs', icon: Film, group: 'using', testId: 'guide-nle',
    visible: (perms) => perms.includes('export.create') && perms.includes('asset.download'),
    Component: lazy(() => import('./guides/NleGuide')) },
  { to: 'quality-control', label: 'Quality control', icon: BadgeCheck, group: 'using', testId: 'guide-qc', visible: everyone,
    Component: lazy(() => import('./guides/QualityControlGuide')) },
  { to: 'masters', label: 'Masters', icon: Layers, group: 'using', testId: 'guide-masters', visible: everyone,
    Component: lazy(() => import('./guides/MastersGuide')) },
  // 1.8.0: for everyone; its administration sections are gated by permission inside.
  { to: 'rights', label: 'Rights', icon: Copyright, group: 'using', testId: 'guide-rights', visible: everyone,
    Component: lazy(() => import('./guides/RightsGuide')) },
  { to: 'workflows', label: 'Workflows', icon: Workflow, group: 'using', testId: 'guide-workflows',
    visible: (perms) => perms.includes('automation.manage') || perms.includes('automation.run'),
    Component: lazy(() => import('./guides/WorkflowsGuide')) },
  { to: 'graphics', label: 'Graphics', icon: Shapes, group: 'using', testId: 'guide-graphics', visible: has('asset.upload'),
    Component: lazy(() => import('./guides/GraphicsGuide')) },
  // 1.3.0: the media portal's uploads, for reviewers.
  { to: 'intake', label: 'Intake', icon: Inbox, group: 'using', testId: 'guide-intake', visible: has('portal.review'),
    Component: lazy(() => import('./guides/IntakeGuide')) },
  { to: 'account', label: 'My account', icon: UserRound, group: 'using', testId: 'guide-account', visible: everyone,
    Component: lazy(() => import('./guides/AccountGuide')) },

  { to: 'admin-storage', label: 'Admin · Storage', icon: HardDrive, group: 'admin', testId: 'guide-admin-storage', visible: has('storage.manage'),
    Component: lazy(() => import('./guides/AdminStorageGuide')) },
  { to: 'sidecars', label: 'Sidecar metadata', icon: FileCode2, group: 'admin', testId: 'guide-sidecars', visible: has('storage.manage'),
    Component: lazy(() => import('./guides/SidecarGuide')) },
  { to: 'storage-migrations', label: 'Storage migrations', icon: ArrowRightLeft, group: 'admin', testId: 'guide-storage-migrations', visible: has('admin'),
    Component: lazy(() => import('./guides/StorageMigrationsGuide')) },
  // Groups is also for access.manage in Administration, but the guide is for administrators.
  { to: 'admin-access', label: 'Admin · Access', icon: ShieldCheck, group: 'admin', testId: 'guide-admin-access', visible: has('admin'),
    Component: lazy(() => import('./guides/AdminAccessGuide')) },
  { to: 'admin-system', label: 'Admin · System', icon: Server, group: 'admin', testId: 'guide-admin-system', visible: has('admin'),
    Component: lazy(() => import('./guides/AdminSystemGuide')) },
  { to: 'admin-portal', label: 'Admin · Media portal', icon: Truck, group: 'admin', testId: 'guide-admin-portal', visible: has('portal.manage'),
    Component: lazy(() => import('./guides/AdminPortalGuide')) },

  // People who make API keys or integrations.
  { to: 'api', label: 'API reference', icon: Braces, group: 'reference', testId: 'api-reference',
    visible: (perms) => perms.includes('admin') || perms.includes('automation.manage'),
    Component: lazy(() => import('./ApiReference').then((m) => ({ default: m.ApiReference }))) },
  { to: 'about', label: 'About', icon: Info, group: 'reference', testId: 'about', visible: everyone,
    Component: lazy(() => import('./About').then((m) => ({ default: m.About }))) },
];

/** The guides the person can open, in tab order. Introduction is always first. */
export function visibleGuides(perms: readonly Permission[]): Guide[] {
  return GUIDES.filter((g) => g.visible(perms));
}

/** Whether the guide at this route exists and the person can open it. */
export function guideVisible(to: string, perms: readonly Permission[]): boolean {
  return GUIDES.some((g) => g.to === to && g.visible(perms));
}

/**
 * What Help shows at /help/<sub> (1.3.0 fix): nothing yet (`wait`) while the signed-in person is not known, so a guide
 * is never judged hidden before the permissions arrive; the guide when the person can open it; otherwise a redirect
 * to the first guide they can see (the Introduction, which everyone sees). A redirect never points at the address it
 * came from, so Help cannot redirect in a loop.
 */
export type HelpTarget = { kind: 'wait' } | { kind: 'guide'; guide: Guide } | { kind: 'redirect'; to: string };

export function helpTarget(sub: string, perms: readonly Permission[] | undefined): HelpTarget {
  if (!perms) return { kind: 'wait' };
  const route = sub.replace(/^\/+|\/+$/g, '');
  const guides = visibleGuides(perms);
  const guide = guides.find((g) => g.to === route);
  if (guide) return { kind: 'guide', guide };
  const first = guides[0] ?? GUIDES[0]!;
  // Only reachable if `first` were not visible; it keeps a redirect to the same address impossible.
  return first.to === route ? { kind: 'guide', guide: first } : { kind: 'redirect', to: `/help/${first.to}` };
}
